/**
 * ConnectedAppsSection - the apps (e.g. claude.ai) the user has connected to coSow
 * through the MCP connector's OAuth sign-in (COS-24), with one-click Disconnect.
 *
 * Disconnecting revokes the grant in the API; the app's next call is rejected and
 * it asks the user to connect again. Reuses the Settings page's profile/security
 * row styles so it reads like the rest of Settings.
 */
import type { OAuthGrantSummary } from '@protoss/shared';
import { Button } from '@protoss/ui';
import * as React from 'react';
import { ApiError, apiFetch } from '../../lib/apiClient';
import { friendlyErrorMessage } from '../../lib/error';

type LoadState = 'loading' | 'loaded' | 'unavailable' | 'error';

function formatDate(iso: string | null): string {
  if (!iso) return 'never';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function ConnectedAppsSection() {
  const [grants, setGrants] = React.useState<OAuthGrantSummary[]>([]);
  const [state, setState] = React.useState<LoadState>('loading');
  const [busy, setBusy] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    try {
      const res = await apiFetch<{ grants: OAuthGrantSummary[] }>('/api/oauth/grants');
      setGrants(res.grants);
      setState('loaded');
    } catch (err) {
      // 404 = OAuth not configured on this deployment.
      setState(err instanceof ApiError && err.statusCode === 404 ? 'unavailable' : 'error');
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  async function disconnect(uuid: string) {
    setBusy(uuid);
    setError(null);
    try {
      await apiFetch(`/api/oauth/grants/${uuid}`, { method: 'DELETE' });
      setGrants((g) => g.filter((x) => x.uuid !== uuid));
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not disconnect the app. Please try again.'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="settings-profile">
      <div className="settings-profile-group">
        <h2 className="settings-profile-group-title">Connected apps</h2>

        {state === 'loading' && <p className="settings-security-hint">Loading...</p>}
        {state === 'unavailable' && (
          <p className="settings-security-hint">
            Connected apps are not enabled on this deployment.
          </p>
        )}
        {state === 'error' && (
          <p className="settings-security-hint settings-security-hint--error">
            Could not load your connected apps.
          </p>
        )}
        {state === 'loaded' && grants.length === 0 && (
          <p className="settings-security-hint">
            No apps are connected. When you connect coSow from Claude, it shows up here.
          </p>
        )}
        {error && <p className="settings-security-hint settings-security-hint--error">{error}</p>}

        {grants.map((g) => (
          <div key={g.uuid} className="settings-security-row">
            <div className="settings-security-info">
              <p className="settings-security-label">
                {g.clientHost} - {g.workspaceName}
              </p>
              <p className="settings-security-hint">
                Connected {formatDate(g.createdAt)} · last used {formatDate(g.lastUsedAt)}
              </p>
            </div>
            <Button
              variant="secondary"
              onClick={() => void disconnect(g.uuid)}
              disabled={busy === g.uuid}
            >
              {busy === g.uuid ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
